import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import CategoryManagerDialog from "@/components/CategoryManagerDialog";

/**
 * CategoryPicker — searchable dropdown bound to /api/categories.
 *
 * Props:
 *  - value: slug of the currently selected category
 *  - onChange(slug): emits the new slug
 *  - canManage: boolean — show the "manage categories" button (admin only)
 *  - onManagerClose(): optional callback fired when the manager dialog is closed
 *    so the parent can re-fetch its own categories list (which holds the schema).
 */
export default function CategoryPicker({ value, onChange, canManage = true, onManagerClose }) {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showManager, setShowManager] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/categories");
      setCats(data || []);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn("categories load failed", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const closeManager = () => {
    setShowManager(false);
    reload();
    if (onManagerClose) onManagerClose();
  };

  const currentSlug = value || "";
  const known = cats.find((c) => c.slug === currentSlug);

  return (
    <div>
      <label className="editorial-label">category</label>
      <div className="flex items-center gap-2 border-b border-taupe mt-2 py-1">
        <select
          data-testid="field-category"
          value={currentSlug}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent py-1 focus:outline-none text-charcoal"
        >
          <option value="">— select —</option>
          {cats.map((c) => (
            <option key={c.slug} value={c.slug}>{c.name.toLowerCase()}</option>
          ))}
          {!known && currentSlug && (
            <option value={currentSlug}>{currentSlug} (legacy)</option>
          )}
        </select>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowManager(true)}
            data-testid="manage-categories-btn"
            className="text-[0.6rem] tracking-widest uppercase border border-taupe text-charcoal hover:border-brass hover:text-brass px-2 py-1"
          >
            manage
          </button>
        )}
      </div>
      {loading && <p className="text-[0.6rem] text-taupe mt-1 tracking-widest uppercase">loading…</p>}
      {showManager && <CategoryManagerDialog onClose={closeManager} />}
    </div>
  );
}
